import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { SelectButton } from 'primereact/selectbutton';

const MODE_OPTIONS = [
    { label: 'Vincular usuario existente', value: 'existing' },
    { label: 'Crear usuario nuevo', value: 'new' }
];

const getUserName = (user) => user?.name || [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim() || user?.email || 'Usuario';
const getRoleLabel = (user) => user?.role?.title || user?.role?.uid || 'Sin perfil';

const AddSellerDialog = ({
    visible,
    candidates,
    loadingCandidates,
    assigning,
    onHide,
    onRefreshCandidates,
    onAssign,
    onCreateInUsers
}) => {
    const [mode, setMode] = useState('existing');
    const [selectedUserId, setSelectedUserId] = useState('');
    const [confirmRoleReplacement, setConfirmRoleReplacement] = useState(false);

    useEffect(() => {
        if (!visible) {
            setMode('existing');
            setSelectedUserId('');
            setConfirmRoleReplacement(false);
        }
    }, [visible]);

    const selectedUser = useMemo(
        () => (candidates || []).find((user) => user._id === selectedUserId) || null,
        [candidates, selectedUserId]
    );
    const requiresRoleConfirmation = Boolean(selectedUser?.role?.uid && selectedUser.role.uid !== 'seller');

    const userOptionTemplate = (option) => (
        <div className="ventas-seller-option">
            <strong>{getUserName(option)}</strong>
            <span>{option.email || '-'}</span>
            <small>Perfil actual: {getRoleLabel(option)}</small>
        </div>
    );

    const selectedUserTemplate = (option) => {
        if (!option) return 'Selecciona un usuario';
        return `${getUserName(option)} · ${option.email || '-'} · ${getRoleLabel(option)}`;
    };

    const handleAssign = () => {
        if (!selectedUserId || assigning) return;
        onAssign({
            userId: selectedUserId,
            confirmRoleReplacement: requiresRoleConfirmation ? confirmRoleReplacement : undefined
        });
    };

    return (
        <Dialog
            header="Agregar vendedor"
            visible={visible}
            onHide={onHide}
            draggable={false}
            className="ventas-dialog ventas-add-seller-dialog"
            style={{ width: '42rem' }}
            breakpoints={{ '960px': '90vw', '640px': '96vw' }}
        >
            <div className="ventas-visit-form">
                <div className="field">
                    <label className="block mb-2" htmlFor="seller-add-mode">Modo</label>
                    <SelectButton
                        id="seller-add-mode"
                        value={mode}
                        options={MODE_OPTIONS}
                        onChange={(event) => {
                            setMode(event.value || 'existing');
                            setSelectedUserId('');
                            setConfirmRoleReplacement(false);
                        }}
                        className="w-full ventas-add-seller-mode"
                    />
                </div>

                {mode === 'existing' ? (
                    <>
                        <div className="field">
                            <div className="flex align-items-center justify-content-between gap-2 mb-2">
                                <label htmlFor="seller-candidate" className="m-0">Usuario existente</label>
                                <Button
                                    type="button"
                                    icon="pi pi-refresh"
                                    className="p-button-text p-button-sm"
                                    onClick={onRefreshCandidates}
                                    disabled={loadingCandidates || assigning}
                                    aria-label="Actualizar usuarios elegibles"
                                />
                            </div>
                            <Dropdown
                                id="seller-candidate"
                                value={selectedUserId}
                                options={candidates || []}
                                optionLabel="name"
                                optionValue="_id"
                                itemTemplate={userOptionTemplate}
                                valueTemplate={selectedUserTemplate}
                                onChange={(event) => {
                                    setSelectedUserId(event.value || '');
                                    setConfirmRoleReplacement(false);
                                }}
                                filter
                                loading={loadingCandidates}
                                disabled={loadingCandidates || assigning}
                                placeholder="Selecciona un usuario activo"
                                emptyMessage="No hay usuarios elegibles"
                                className="w-full"
                            />
                        </div>

                        {selectedUser ? (
                            <div className="surface-50 border-round p-3 mb-3">
                                <div className="font-semibold">{getUserName(selectedUser)}</div>
                                <div className="text-sm text-color-secondary">{selectedUser.email || '-'}</div>
                                <div className="text-sm mt-2">Perfil actual: <strong>{getRoleLabel(selectedUser)}</strong></div>
                            </div>
                        ) : null}

                        {requiresRoleConfirmation ? (
                            <div className="mb-3">
                                <Message
                                    severity="warn"
                                    text={`Syncro maneja actualmente un solo role por usuario. Este usuario perderá el perfil ${getRoleLabel(selectedUser)} y será asignado como Vendedor.`}
                                />
                                <div className="flex align-items-start gap-2 mt-3">
                                    <Checkbox
                                        inputId="confirm-role-replacement"
                                        checked={confirmRoleReplacement}
                                        onChange={(event) => setConfirmRoleReplacement(Boolean(event.checked))}
                                        disabled={assigning}
                                    />
                                    <label htmlFor="confirm-role-replacement" className="line-height-3">
                                        Confirmo que este usuario será asignado como Vendedor.
                                    </label>
                                </div>
                            </div>
                        ) : null}

                        <div className="flex flex-column sm:flex-row justify-content-end gap-2">
                            <Button type="button" label="Cancelar" icon="pi pi-times" className="p-button-secondary" onClick={onHide} disabled={assigning} />
                            <Button
                                type="button"
                                label="Guardar vendedor"
                                icon="pi pi-user-plus"
                                onClick={handleAssign}
                                loading={assigning}
                                disabled={!selectedUserId || assigning || (requiresRoleConfirmation && !confirmRoleReplacement)}
                            />
                        </div>
                    </>
                ) : (
                    <>
                        <Message
                            severity="info"
                            text="La creación de usuarios se realiza en el módulo Usuarios para reutilizar sus reglas de contraseña, email y perfil."
                        />
                        <div className="surface-50 border-round p-3 my-3">
                            <strong>Perfil recomendado: Vendedor</strong>
                            <p className="text-color-secondary mt-2 mb-0">
                                Crea el usuario en Usuarios y selecciona el perfil Vendedor. Después aparecerá automáticamente en esta lista.
                            </p>
                        </div>
                        <div className="flex flex-column sm:flex-row justify-content-end gap-2">
                            <Button type="button" label="Cancelar" icon="pi pi-times" className="p-button-secondary" onClick={onHide} />
                            <Button type="button" label="Crear usuario en Usuarios" icon="pi pi-external-link" onClick={onCreateInUsers} />
                        </div>
                    </>
                )}
            </div>
        </Dialog>
    );
};

export default AddSellerDialog;
